import { useEffect } from "react";
import {
  profileThemeBackgroundCss,
  profileThemeIsLightText,
  profileThemeTextColor,
  type ProfileTheme,
} from "@scroll/shared-types";

type SurroundEntry = {
  theme: ProfileTheme | null;
  active: boolean;
  priority: number;
  order: number;
};

// Multiple components can broadcast a candidate theme at once (the signed-in
// user's own theme from the app shell at priority 0, the viewed creator's theme
// from a profile page at priority 1). They register here; the highest-priority
// active entry with a usable background wins and tints the chrome.
const entries = new Map<string, SurroundEntry>();
let nextOrder = 0;

function clearSurround(root: HTMLElement) {
  root.classList.remove("profile-surround-active");
  root.style.removeProperty("--profile-surround");
  root.style.removeProperty("--profile-surround-text");
  root.style.removeProperty("--profile-surround-dim");
  root.style.removeProperty("--profile-surround-line");
}

function applySurround() {
  const root = document.documentElement;
  const selected = Array.from(entries.values())
    .filter((entry) => entry.active && profileThemeBackgroundCss(entry.theme))
    .sort((a, b) => b.priority - a.priority || b.order - a.order)[0];

  const bg = selected ? profileThemeBackgroundCss(selected.theme) : null;
  if (!selected || !bg) {
    clearSurround(root);
    return;
  }

  const theme = selected.theme;
  const light = profileThemeIsLightText(theme);
  root.style.setProperty("--profile-surround", bg);
  root.style.setProperty("--profile-surround-text", profileThemeTextColor(theme) || "#ffffff");
  root.style.setProperty("--profile-surround-dim", light ? "rgba(255,255,255,0.66)" : "rgba(29,25,22,0.55)");
  root.style.setProperty("--profile-surround-line", light ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.12)");
  root.classList.add("profile-surround-active");
}

/**
 * Broadcast a candidate theme to tint the app chrome (sidenav, rail, gutters).
 * The chrome-only surround CSS (.profile-surround-active in index.css) reads the
 * vars this sets on <html>; page content is never tinted. Higher `priority` wins
 * when several sources are active — e.g. a viewed profile (1) overrides the
 * signed-in user's default (0).
 */
export function useProfileSurroundTheme(
  source: string,
  theme: ProfileTheme | null | undefined,
  active: boolean,
  priority = 0,
) {
  useEffect(() => {
    const existing = entries.get(source);
    entries.set(source, {
      theme: theme ?? null,
      active,
      priority,
      order: existing?.order ?? nextOrder++,
    });
    applySurround();

    return () => {
      entries.delete(source);
      applySurround();
    };
  }, [source, theme, active, priority]);
}
